import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'

import { ArrowDownIcon, ChevronLeftIcon, ChevronRightIcon } from '~assets/icons'

import './common.less'

interface PaginationProps {
  totalCount: number
  defaultPageSize: number
  onPageChange: (page: number, pageSize: number) => void
  pageSizeOptions?: number[]
  type?: string
  onPageSizeChange?: (pageSize: number) => void
  currentPage?: number
  currentPageSize?: number
}

const Pagination: React.FC<PaginationProps> = ({
  totalCount,
  defaultPageSize,
  onPageChange,
  pageSizeOptions,
  type,
  onPageSizeChange,
  currentPage: externalCurrentPage,
  currentPageSize: externalCurrentPageSize,
}) => {
  const [internalCurrentPage, setInternalCurrentPage] = useState(0)
  const [internalPageSize, setInternalPageSize] = useState(defaultPageSize)

  const currentPage = externalCurrentPage !== undefined ? externalCurrentPage : internalCurrentPage
  const pageSize =
    externalCurrentPageSize !== undefined ? externalCurrentPageSize : internalPageSize
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [customPageSize, setCustomPageSize] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const maxPage = Math.max(0, Math.ceil(totalCount / pageSize) - 1)

  useEffect(() => {
    if (externalCurrentPageSize === undefined && internalPageSize !== defaultPageSize) {
      setInternalPageSize(defaultPageSize)
    }
  }, [defaultPageSize, externalCurrentPageSize, internalPageSize])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const dropdown = document.getElementById('custom-dropdown')
      if (dropdown && !dropdown.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return (): void => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    if (event.target.value === 'custom') {
      setIsModalOpen(true)
      return
    }
    const newSize = parseInt(event.target.value, 10)
    const newPage = Math.floor((currentPage * pageSize) / newSize)
    if (externalCurrentPageSize === undefined) {
      setInternalPageSize(newSize)
    }
    if (externalCurrentPage === undefined) {
      setInternalCurrentPage(newPage)
    }
    onPageSizeChange?.(newSize)
    onPageChange(newPage, newSize)
  }

  const handlePageChange = (newPage: number, pageSize: number): void => {
    if (externalCurrentPage === undefined) {
      setInternalCurrentPage(newPage)
    }
    onPageChange(newPage, pageSize)
  }

  const handleCustomPageSizeSubmit = (): void => {
    const newSize = parseInt(customPageSize, 10)
    if (!isNaN(newSize) && newSize > 0) {
      const newPage = Math.floor((currentPage * pageSize) / newSize)
      if (externalCurrentPageSize === undefined) {
        setInternalPageSize(newSize)
      }
      if (externalCurrentPage === undefined) {
        setInternalCurrentPage(newPage)
      }
      onPageSizeChange?.(newSize)
      onPageChange(newPage, newSize)
    }
    setIsModalOpen(false)
    setCustomPageSize('')
  }

  return (
    <div className='pagination-container'>
      <div className='total-count'>
        <span>
          Showing {currentPage * pageSize} - {Math.min((currentPage + 1) * pageSize, totalCount)} of
        </span>
        <span>{totalCount}</span>
        <span style={{ marginLeft: '0.25em', marginRight: '0.25em' }}> {type}</span>
      </div>
      {pageSizeOptions && (
        <div className='page-size-dropdown' id='custom-dropdown' style={{ position: 'relative' }}>
          <IconButton
            size='small'
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            style={{
              transform: isDropdownOpen ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
            }}
          >
            <ArrowDownIcon width={20} height={20} color='var(--lightcarbon)' />
          </IconButton>
          {isDropdownOpen && (
            <div
              className='dropdown-options'
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                minWidth: '120px',
                zIndex: 1000,
              }}
            >
              {pageSizeOptions.map((size) => (
                <Box
                  key={size}
                  onClick={() => {
                    handlePageSizeChange({
                      target: { value: size.toString() },
                    } as React.ChangeEvent<HTMLSelectElement>)
                    setIsDropdownOpen(false)
                  }}
                  sx={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    '&:first-of-type': {
                      borderTopLeftRadius: '8px',
                      borderTopRightRadius: '8px',
                    },
                    '&:last-child': {
                      borderBottomLeftRadius: '8px',
                      borderBottomRightRadius: '8px',
                    },
                  }}
                >
                  {size} {type}
                </Box>
              ))}
              <Box
                onClick={() => {
                  handlePageSizeChange({
                    target: { value: 'custom' },
                  } as React.ChangeEvent<HTMLSelectElement>)
                  setIsDropdownOpen(false)
                }}
                sx={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                  borderBottomLeftRadius: '8px',
                  borderBottomRightRadius: '8px',
                }}
              >
                Custom
              </Box>
            </div>
          )}
        </div>
      )}
      <IconButton onClick={() => handlePageChange(Math.max(0, currentPage - 1), pageSize)}>
        <ChevronLeftIcon width={24} height={24} color='var(--lightcarbon)' />
      </IconButton>
      {currentPage > 1 && (
        <>
          <div className='page-number' onClick={() => handlePageChange(0, pageSize)}>
            1
          </div>
          <div className='dots'>...</div>
        </>
      )}
      {currentPage > 0 && (
        <div className='page-number' onClick={() => handlePageChange(currentPage - 1, pageSize)}>
          {currentPage}
        </div>
      )}
      <div className='page-number active'>{currentPage + 1}</div>
      {currentPage < maxPage && (
        <div className='page-number' onClick={() => handlePageChange(currentPage + 1, pageSize)}>
          {currentPage + 2}
        </div>
      )}
      {currentPage < maxPage - 1 && (
        <>
          <div className='dots'>...</div>
          <div className='page-number' onClick={() => handlePageChange(maxPage, pageSize)}>
            {maxPage + 1}
          </div>
        </>
      )}
      <IconButton onClick={() => handlePageChange(Math.min(maxPage, currentPage + 1), pageSize)}>
        <ChevronRightIcon width={24} height={24} color='var(--lightcarbon)' />
      </IconButton>

      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        sx={{ '& .MuiDialog-paper': { width: '300px', padding: '25px' } }}
      >
        <DialogTitle sx={{ padding: '0px' }}>Enter Custom Page Size</DialogTitle>
        <DialogContent sx={{ padding: '0px' }}>
          <Typography variant='body2' color='text.secondary'>
            Larger values can lead to longer loading times
          </Typography>
          <TextField
            autoFocus
            type='number'
            value={customPageSize}
            onChange={(e) => setCustomPageSize(e.target.value)}
            inputProps={{ min: '1' }}
            fullWidth
            margin='dense'
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-start', padding: '10px 0px 0px' }}>
          <Button onClick={handleCustomPageSizeSubmit} variant='contained' color='secondary'>
            Apply
          </Button>
          <Button onClick={() => setIsModalOpen(false)} variant='text' color='secondary'>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default Pagination
